<script setup>
import { usePublicTheme } from '@/composables/usePublicTheme'

const themeStore = usePublicTheme()

const lastUpdate = '18 de agosto de 2026'
</script>

<template>
  <div class="legal-page">
    <button
      type="button"
      class="theme-toggle-btn"
      :title="themeStore.theme === 'dark' ? 'Modo claro' : 'Modo oscuro'"
      @click="themeStore.toggle()"
    >
      <i :class="themeStore.theme === 'dark' ? 'pi pi-sun' : 'pi pi-moon'" />
    </button>

    <div class="legal-container">
      <header class="legal-header">
        <img src="/territorios-icono.png" alt="Logo" class="legal-logo">
        <h1 class="legal-title">Términos, privacidad y uso de datos</h1>
        <p class="legal-updated">Última actualización: {{ lastUpdate }}</p>
      </header>

      <nav class="legal-toc">
        <a href="#terminos">Términos y condiciones</a>
        <a href="#privacidad">Política de privacidad</a>
        <a href="#datos">Uso de datos</a>
      </nav>

      <section id="terminos" class="legal-section">
        <h2>1. Términos y condiciones de uso</h2>

        <h3>1.1 Aceptación</h3>
        <p>
          Territorios es una herramienta interna, provista sin costo, para que las congregaciones
          organicen la predicación: administración de territorios, planificación de salidas,
          asignación de estudios, alojamientos y transporte durante asambleas o visitas. Al acceder
          a la aplicación o a cualquiera de sus enlaces públicos (salidas, estudios, alojamientos,
          registro de invitados) se aceptan estos términos.
        </p>

        <h3>1.2 Naturaleza del servicio</h3>
        <p>
          El servicio se ofrece "tal cual" y "según disponibilidad", sin garantías de
          funcionamiento ininterrumpido, ausencia de errores o disponibilidad continua. No forma
          parte de ninguna organización religiosa ni actúa en su representación: es una herramienta
          construida y mantenida de manera independiente para facilitar tareas administrativas de
          las congregaciones que optan por usarla.
        </p>

        <h3>1.3 Uso permitido</h3>
        <p>
          El acceso a cada congregación es administrado por sus propios usuarios (superintendentes,
          administradores o encargados designados). Cada congregación es responsable de a quién le
          otorga acceso, de la veracidad de los datos que carga (domicilios, territorios, nombres de
          publicadores, asignaciones) y del uso adecuado de la información obtenida a través de la
          aplicación, incluyendo direcciones y datos de mapas de terceros (por ejemplo, Google Maps
          u OpenStreetMap).
        </p>

        <h3>1.4 Enlaces públicos temporales</h3>
        <p>
          Algunas funciones generan enlaces de acceso público sin necesidad de iniciar sesión (por
          ejemplo, para compartir una salida, un estudio o un alojamiento con un grupo de invitados).
          Cualquier persona que reciba ese enlace puede ver la información que contiene mientras el
          enlace permanezca activo. Quien genera el enlace es responsable de compartirlo únicamente
          con las personas a las que está destinado y de revocarlo cuando ya no sea necesario.
        </p>

        <h3>1.5 Limitación de responsabilidad</h3>
        <p>
          En la máxima medida permitida por la ley, no se asume responsabilidad por daños directos
          o indirectos derivados del uso o la imposibilidad de uso de la aplicación, incluyendo
          errores en direcciones, rutas o datos de terceros, pérdida de datos, interrupciones del
          servicio, o decisiones tomadas en base a la información disponible en la aplicación. El
          uso de la aplicación durante la actividad de predicación (por ejemplo, navegación o
          transporte) es responsabilidad exclusiva de cada usuario.
        </p>

        <h3>1.6 Cambios y disponibilidad</h3>
        <p>
          El servicio puede modificarse, suspenderse o discontinuarse en cualquier momento, total o
          parcialmente, sin que ello genere derecho a compensación alguna. Estos términos pueden
          actualizarse; el uso continuado de la aplicación luego de una actualización implica su
          aceptación.
        </p>
      </section>

      <section id="privacidad" class="legal-section">
        <h2>2. Política de privacidad</h2>

        <h3>2.1 Qué información se recopila</h3>
        <p>
          Según el módulo que se utilice, la aplicación puede almacenar: credenciales de acceso
          (email y contraseña con hash), datos de territorios (domicilios, límites geográficos,
          notas de predicación), registros de salidas y asistencia, asignaciones de estudios,
          datos de alojamiento y transporte, e invitaciones o registros de invitados.
        </p>

        <h3>2.2 Cifrado de los datos sensibles</h3>
        <p>
          Los datos considerados sensibles (por ejemplo, información detallada de territorios) se
          cifran en el propio dispositivo del usuario antes de enviarse al servidor, usando una
          clave que no es accesible por el equipo que administra la aplicación. Esto significa que,
          en condiciones normales, nadie fuera de la congregación puede leer esa información aunque
          tenga acceso a la base de datos.
        </p>

        <h3>2.3 Con quién se comparte la información</h3>
        <p>
          Los datos no se venden ni se ceden a terceros con fines comerciales o publicitarios.
          Se comparten únicamente con los proveedores de infraestructura estrictamente necesarios
          para operar el servicio (por ejemplo, alojamiento del servidor y base de datos), y con
          servicios de mapas de terceros cuando se solicitan direcciones o rutas, sujeto a las
          políticas de privacidad de esos proveedores.
        </p>

        <h3>2.4 Almacenamiento local y cookies</h3>
        <p>
          La aplicación utiliza almacenamiento local del navegador (localStorage) para recordar
          preferencias como el tema de color, el estado de la sesión y ciertos datos de trabajo
          offline. No se utilizan cookies de rastreo publicitario ni analítica de terceros.
        </p>

        <h3>2.5 Derechos del usuario</h3>
        <p>
          Cualquier publicador puede solicitar al administrador de su congregación el acceso,
          la corrección o la eliminación de sus datos personales dentro de la aplicación. Los
          administradores de congregación pueden eliminar cuentas, territorios o registros desde
          el panel correspondiente.
        </p>

        <h3>2.6 Menores de edad</h3>
        <p>
          La aplicación está pensada para uso administrativo por parte de publicadores adultos o
          responsables designados por la congregación. No está dirigida a la recolección
          intencional de datos de menores fuera de ese contexto.
        </p>
      </section>

      <section id="datos" class="legal-section">
        <h2>3. Uso de datos</h2>

        <h3>3.1 Finalidad</h3>
        <p>
          Los datos cargados se utilizan exclusivamente para el funcionamiento de la aplicación
          dentro de la congregación que los ingresó: asignar y hacer seguimiento de territorios,
          coordinar salidas, gestionar estudios bíblicos y organizar aspectos logísticos como
          transporte o alojamiento durante asambleas.
        </p>

        <h3>3.2 Aislamiento entre congregaciones</h3>
        <p>
          Cada congregación accede únicamente a sus propios datos. La información de una
          congregación no es visible para otra, salvo en los casos explícitos de enlaces públicos
          generados intencionalmente por sus propios administradores.
        </p>

        <h3>3.3 Retención</h3>
        <p>
          Los datos se conservan mientras la congregación mantenga su cuenta activa. Ante una
          solicitud de baja, o por inactividad prolongada, la información puede eliminarse de
          forma definitiva de los servidores.
        </p>

        <h3>3.4 Uso responsable</h3>
        <p>
          Se recomienda a cada congregación no cargar más información personal de la estrictamente
          necesaria para la actividad de predicación, y revisar periódicamente los enlaces públicos
          activos, revocando los que ya no se utilicen.
        </p>

        <h3>3.5 Contacto</h3>
        <p>
          Ante dudas sobre estos términos, la política de privacidad o el uso de datos, podés
          contactar al administrador de tu congregación o escribir por
          <a
            href="https://api.whatsapp.com/send?phone=5492644766180&text=Hola,%20tengo%20una%20consulta%20sobre%20privacidad%20y%20uso%20de%20datos."
            target="_blank"
            rel="noopener noreferrer"
          >WhatsApp</a>.
        </p>
      </section>

      <footer class="legal-footer">
        <a href="/">Volver al inicio</a>
      </footer>
    </div>
  </div>
</template>

<style scoped>
.legal-page {
  position: relative;
  min-height: 100vh;
  background: var(--bg-grouped, #f2f2f7);
  padding: 32px 20px 64px;
}

.theme-toggle-btn {
  position: fixed;
  top: max(env(safe-area-inset-top, 0px), 16px);
  right: 16px;
  z-index: 10;
  width: 38px;
  height: 38px;
  border-radius: 50%;
  border: 1.5px solid var(--separator, rgba(0, 0, 0, 0.15));
  background: var(--bg-primary, #fff);
  color: var(--label-secondary, #666);
  font-size: 16px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  -webkit-tap-highlight-color: transparent;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.theme-toggle-btn:active {
  opacity: 0.7;
}

.legal-container {
  max-width: 720px;
  margin: 0 auto;
  text-align: left;
}

.legal-header {
  text-align: center;
  margin-bottom: 28px;
}

.legal-logo {
  width: 72px;
  height: 72px;
  margin-bottom: 12px;
}

.legal-title {
  font-size: 24px;
  font-weight: 700;
  color: var(--label-primary, #111);
  margin: 0 0 6px;
}

.legal-updated {
  font-size: 13px;
  color: var(--label-secondary, #666);
  margin: 0;
}

.legal-toc {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  justify-content: center;
  margin-bottom: 28px;
}

.legal-toc a {
  font-size: 13px;
  font-weight: 600;
  color: var(--accent, #007aff);
  background: var(--bg-primary, #fff);
  border: 1px solid var(--separator, rgba(0, 0, 0, 0.12));
  border-radius: 999px;
  padding: 6px 14px;
  text-decoration: none;
}

.legal-section {
  background: var(--bg-primary, #fff);
  border-radius: 16px;
  padding: 24px;
  margin-bottom: 20px;
  scroll-margin-top: 20px;
}

.legal-section h2 {
  font-size: 19px;
  font-weight: 700;
  color: var(--label-primary, #111);
  margin: 0 0 16px;
}

.legal-section h3 {
  font-size: 15px;
  font-weight: 600;
  color: var(--label-primary, #111);
  margin: 18px 0 6px;
}

.legal-section h3:first-of-type {
  margin-top: 0;
}

.legal-section p {
  font-size: 14px;
  line-height: 1.6;
  color: var(--label-secondary, #444);
  margin: 0;
}

.legal-section a {
  color: var(--accent, #007aff);
}

.legal-footer {
  text-align: center;
  margin-top: 12px;
}

.legal-footer a {
  font-size: 14px;
  font-weight: 600;
  color: var(--accent, #007aff);
  text-decoration: none;
}
</style>
